
import { computed, defineComponent, onMounted } from "vue";
import { setCurrentPageTitle } from "@/core/helpers/breadcrumb";
import ApiService from "@/core/services/ApiService";
import { ElNotification } from "element-plus";
import { saveAs } from "file-saver";
import { useStore } from "vuex";

type ReportKind = "outstanding" | "supplied";
type ProductType = "loan" | "line";

interface OptionItem {
  value: string;
  label: string;
  type?: string;
  name?: string;
}

export default defineComponent({
  name: "ReportIndex",
  setup() {
    const store = useStore();
    onMounted(() => {
      setCurrentPageTitle("Үндсэн тайлан");
    });

    const orgname = computed(() => store.getters.orgName);
    const orgcode = computed(() => store.getters.orgCode);
    const roles = computed(() => store.getters.getRoleNames || []);
    const supervisor = computed(() => {
      return (
        roles.value.includes("ROLE_REPORT_SV") ||
        roles.value.includes("ROLE_ADMIN")
      );
    });
    const admin = computed(() => roles.value.includes("ROLE_ADMIN"));

    return {
      orgname,
      orgcode,
      roles,
      supervisor,
      admin
    };
  },
  data() {
    const now = new Date();
    const end = this.formatDate(now);
    const startDate = new Date(
      now.getFullYear(),
      now.getMonth() - 1,
      now.getDate()
    );
    const start = this.formatDate(startDate);
    return {
      reportKind: "outstanding" as ReportKind,
      productType: "loan" as ProductType,
      dateRange: [start, end] as string[] | null,
      downloading: false,
      loadingOrg: false,
      orgcodeValue: "*",
      orgTypeValue: "*",
      orgList: [] as OptionItem[],
      orgTypes: [
        { value: "*", label: "Бүгд" },
        { value: "1", label: "Банк" },
        { value: "2", label: "ББСБ" },
        { value: "3", label: "ХЗХ" },
        { value: "4", label: "Төрийн байгууллага" },
        { value: "5", label: "Үүрэн холбоо" },
        { value: "6", label: "Санхүүгийн түрээс" },
        { value: "7", label: "Даатгалын компани" },
        { value: "8", label: "Үнэт цаасны компани" },
        { value: "9", label: "Бусад" }
      ] as OptionItem[]
    };
  },
  mounted() {
    if (this.admin) {
      this.orgcodeValue = "*";
    } else {
      this.orgcodeValue = "";
    }
    if (this.supervisor) {
      this.getOrg();
    }
  },
  computed: {
    selectedReportName(): string {
      if (this.reportKind === "supplied" && this.productType === "line") {
        return "Нийлүүлсэн шугамын эрхийн тайлан";
      }
      if (this.reportKind === "supplied") {
        return "Нийлүүлсэн зээлийн тайлан";
      }
      if (this.productType === "line") {
        return "Үлдэгдэлтэй шугамын эрхийн тайлан";
      }
      return "Үлдэгдэлтэй зээлийн тайлан";
    },
    dateRangeDisabled(): boolean {
      return this.supervisor && this.reportKind === "outstanding";
    },
    intervalText(): string {
      if (this.dateRangeDisabled) {
        return "Хугацааны хязгааргүй";
      }
      return "Хамгийн ихдээ 6 сар";
    },
    scopeText(): string {
      if (this.supervisor) {
        return "Хяналтын эрхтэй хэрэглэгч байгууллагын төрөл болон байгууллагаар шүүж тайлан татна.";
      }
      return "Тайлан зөвхөн өөрийн байгууллагын олгосон зээлээр гарна.";
    },
    filteredOrgList(): OptionItem[] {
      return this.orgList.filter((item: OptionItem) => {
        return this.orgTypeValue === "*" || item.type === this.orgTypeValue;
      });
    },
    selectedOrgLabel(): string {
      if (!this.orgcodeValue || this.orgcodeValue === "*") {
        return this.orgTypeValue === "*"
          ? "Бүх байгууллага"
          : this.orgTypeName(this.orgTypeValue);
      }
      const selected = this.orgList.find(
        (item: OptionItem) => item.value === this.orgcodeValue
      );
      return selected?.value || "Байгууллага сонгоно уу";
    },
    selectedOrgCodeLabel(): string {
      if (!this.orgcodeValue || this.orgcodeValue === "*") {
        return this.admin ? "Бүх байгууллага" : "Байгууллага сонгоно уу";
      }
      const selected = this.orgList.find(
        (item: OptionItem) => item.value === this.orgcodeValue
      );
      return selected?.name || this.orgcodeValue;
    }
  },
  methods: {
    handleOrgTypeChange() {
      this.orgcodeValue = this.admin ? "*" : "";
    },
    handleKindChange() {
      if (
        !this.dateRangeDisabled &&
        (!this.dateRange || this.dateRange.length !== 2)
      ) {
        const now = new Date();
        const startDate = new Date(
          now.getFullYear(),
          now.getMonth() - 1,
          now.getDate()
        );
        this.dateRange = [this.formatDate(startDate), this.formatDate(now)];
      }
    },
    handleDateRangeChange(value: string[] | null) {
      if (!value || value.length !== 2) {
        this.dateRange = null;
        return;
      }
      const start = new Date(value[0]);
      const end = new Date(value[1]);
      const max = new Date(start);
      max.setMonth(max.getMonth() + 6);
      if (end > max) {
        this.dateRange = [value[0], this.formatDate(max)];
        ElNotification({
          title: "Анхааруулга",
          message: "Хамрах хугацааг эхлэх огнооноос 6 сар болгож тохирууллаа.",
          type: "warning"
        });
      }
    },
    async downloadReport() {
      const validation = this.validateFilter();
      if (validation) {
        ElNotification({
          title: "Анхааруулга",
          message: validation,
          type: "warning"
        });
        return;
      }

      this.downloading = true;
      document.body.classList.add("waiting");

      const params = {
        reportKind: this.reportKind,
        productType: this.productType,
        startDate: this.dateRangeDisabled ? null : this.dateStart(),
        endDate: this.dateRangeDisabled ? null : this.dateEnd(),
        orgType: this.supervisor ? this.orgTypeValue : null,
        orgcode: this.supervisor ? this.orgcodeValue : null
      };

      ApiService.setHeader();
      await ApiService.vueInstance.axios
        .post("reportindex", {}, { params, responseType: "blob" })
        .then(resp => {
          const contentType = resp.headers["content-type"] || "";
          if (contentType.includes("application/json")) {
            this.handleBlobError(resp.data);
          } else {
            saveAs(resp.data, `${this.fileName()}.xlsx`);
          }
        })
        .catch(() => {
          ElNotification({
            title: "Алдаа",
            message: "Тайлан татахад алдаа гарлаа.",
            type: "error"
          });
        })
        .finally(() => {
          document.body.classList.remove("waiting");
          this.downloading = false;
        });
    },
    async getOrg() {
      this.loadingOrg = true;
      await ApiService.get("org")
        .then(resp => {
          const results = resp.data;
          if (results.code === 0) {
            ElNotification({
              title: "Алдаа",
              message: results.error,
              type: "error"
            });
          } else if (results.code === 401) {
            ElNotification({
              title: "Анхааруулга",
              message: results.warning,
              type: "warning"
            });
          } else {
            this.orgList = results.response.map(org => ({
              value: org.orgcode,
              type: org.orgtypecode,
              name: org.orgname,
              label: `${org.orgcode} - ${org.orgname}`
            }));
          }
        })
        .catch(() => {
          ElNotification({
            title: "Алдаа",
            message: "Байгууллагын жагсаалт уншихад алдаа гарлаа.",
            type: "error"
          });
        })
        .finally(() => {
          this.loadingOrg = false;
        });
    },
    handleBlobError(blob: Blob) {
      const reader = new FileReader();
      reader.onload = () => {
        let message = "Тайлан татахад алдаа гарлаа.";
        try {
          const parsed = JSON.parse(String(reader.result || "{}"));
          message = parsed.error || parsed.warning || message;
        } catch (e) {
          message = String(reader.result || message);
        }
        ElNotification({
          title: "Алдаа",
          message,
          type: "error"
        });
      };
      reader.readAsText(blob);
    },
    validateFilter(): string {
      if (this.dateRangeDisabled) {
        if (this.supervisor && !this.admin && !this.orgcodeValue) {
          return "Байгууллага сонгоно уу.";
        }
        return "";
      }
      if (!this.dateRange || this.dateRange.length !== 2) {
        return "Хамрах хугацаа сонгоно уу.";
      }
      if (this.supervisor && !this.admin && !this.orgcodeValue) {
        return "Байгууллага сонгоно уу.";
      }
      const start = new Date(this.dateRange[0]);
      const end = new Date(this.dateRange[1]);
      if (end < start) {
        return "Дуусах огноо эхлэх огнооноос өмнө байж болохгүй.";
      }
      const max = new Date(start);
      max.setMonth(max.getMonth() + 6);
      if (end > max) {
        return "Хамрах хугацаа хамгийн ихдээ 6 сар байна.";
      }
      return "";
    },
    fileName(): string {
      const kind = this.reportKind === "supplied" ? "supplied" : "outstanding";
      const product = this.productType === "line" ? "loanline" : "loan";
      const period = this.dateRangeDisabled
        ? "all"
        : `${this.dateStart()}_${this.dateEnd()}`;
      const org = this.supervisor ? this.orgcodeValue || "org" : this.orgcode;
      return `${kind}_${product}_report_${org}_${period}`;
    },
    dateStart(): string | null {
      return this.dateRange && this.dateRange.length === 2
        ? this.dateRange[0]
        : null;
    },
    dateEnd(): string | null {
      return this.dateRange && this.dateRange.length === 2
        ? this.dateRange[1]
        : null;
    },
    formatDate(value: Date): string {
      const year = value.getFullYear();
      const month = `${value.getMonth() + 1}`.padStart(2, "0");
      const day = `${value.getDate()}`.padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    orgTypeName(value: string): string {
      const found = this.orgTypes.find(
        (item: OptionItem) => item.value === value
      );
      return found?.label || "Бүх байгууллага";
    }
  }
});
